import {match} from 'ts-pattern';

import {isNil, isNotNil} from 'ramda';

import {GetDataByDataQueryApiResponse, KanbanSortBy} from '@dms/api/core';

import {parseDate} from 'shared/web';

import {KANBAN_COLUMN_VALUE_TO_INDEX} from '../constants/kanbanColumns';
import {Card} from '../types/Card';
import {mapBusinessCasesToCards} from './mapBusinessCasesToCards';
import {mapInterestsToCards} from './mapInterestsToCards';

const getSortTimestamp = (card: Card, sortBy: KanbanSortBy) => {
  const value = match(sortBy)
    .with('CREATED_AT_ASC', 'CREATED_AT_DESC', () => card.date)
    .with('LAST_ACTIVITY_ASC', () => card.lastActivityDate)
    .with('LAST_COMMUNICATION_ASC', () => card.lastCommunicationDate)
    .exhaustive();

  return isNil(value) ? null : parseDate(value).getTime();
};

/**
 * Gets cards for a specific column by column value (string)
 * This function uses automatically generated mapping to ensure consistency
 */
export const getCardsForColumnValue = (
  columnValue: string,
  sortBy: KanbanSortBy,
  businessCases?: GetDataByDataQueryApiResponse,
  interests?: GetDataByDataQueryApiResponse
): Card[] => {
  const allCards: Card[] = [];

  if (isNotNil(businessCases)) {
    const businessCaseCards = mapBusinessCasesToCards(businessCases);
    allCards.push(...businessCaseCards);
  }

  if (isNotNil(interests)) {
    const interestCards = mapInterestsToCards(interests);
    allCards.push(...interestCards);
  }

  const targetColumnIndex = KANBAN_COLUMN_VALUE_TO_INDEX[columnValue];

  if (targetColumnIndex === undefined) {
    throw new Error(
      `Unknown columnValue: "${columnValue}". Check KANBAN_COLUMN_VALUE_TO_INDEX mapping.`
    );
  }

  const columnCards = allCards.filter((card) => card.column === targetColumnIndex);

  const timestampById = new Map(
    columnCards.map((card) => [card.id, getSortTimestamp(card, sortBy)])
  );
  const direction = sortBy === 'CREATED_AT_DESC' ? -1 : 1;

  return [...columnCards].sort((cardA, cardB) => {
    const timestampA = timestampById.get(cardA.id);
    const timestampB = timestampById.get(cardB.id);

    if (isNil(timestampA)) {
      return isNil(timestampB) ? 0 : 1;
    }
    if (isNil(timestampB)) {
      return -1;
    }

    return (timestampA - timestampB) * direction;
  });
};
